import {
	Card, Icon, IconSize, IconWeight, Popover, Select, SettingsLayout, Switch
} from "@clintonelec/react-storybook";
import { type FormEvent, Fragment, memo, useState } from "react";
import { deviceNameOptions, colorOptions } from "Data/Objects/EmbeddedOsdOptions";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectEmbeddedOsd, setEmbeddedOsdAction } from "Data/Redux/Slices/Settings/Camera/EmbeddedOsd";
import type { EmbeddedOsdSettingsState } from "Interfaces";
import { Link } from "react-router-dom";
import type { Colors, DeviceNamePosition } from "Data/Objects/Camera";
import { produce } from "immer";
import { isEqual, merge } from "lodash";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import "Components/ConfigLayout/Camera/EmbeddedOsdSettings/EmbeddedOsdSettings.less";

const aiOsdPopover = "AI OSD overlay is only applied to the 4th stream.";
const deviceNamePopover = (
	<Fragment>
		<span>Go to&nbsp;</span>
		<Link to="/setup/system/management">
			System Management
		</Link>
		<span>&nbsp;to set the device name.</span>
	</Fragment>
);

function EmbeddedOsdSettings() {
	const dispatch = useAppDispatch();
	const embeddedOsdSettings = useAppSelector(selectEmbeddedOsd);
	const [ embeddedOsdSettingsDiff, setEmbeddedOsdSettingsDiff ] = useState<Partial<EmbeddedOsdSettingsState>>({});

	const setEmbeddedOsdSettings = (newSettings: EmbeddedOsdSettingsState) => {
		dispatch(setEmbeddedOsdAction(newSettings));
	};

	const localEmbeddedOsdSettings = produce(embeddedOsdSettings, draft => {
		merge(draft, embeddedOsdSettingsDiff);
	});

	const { resolution, dateTime, deviceName } = localEmbeddedOsdSettings.cameraInfo;
	const { displayMode, objectColor, ruleColor, eventColor } = localEmbeddedOsdSettings.ai;
	const disabled = isEqual(localEmbeddedOsdSettings, embeddedOsdSettings);

	const handleReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setEmbeddedOsdSettingsDiff({});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setEmbeddedOsdSettings(localEmbeddedOsdSettings);
		setEmbeddedOsdSettingsDiff({});
	};

	const handleResolutionChecked = (checked: boolean) => {
		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.cameraInfo.resolution = checked;
		}));
	};

	const handleDateTimeChecked = (checked: boolean) => {
		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.cameraInfo.dateTime = checked;
		}));
	};

	const handleDeviceNameSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.cameraInfo.deviceName = value as DeviceNamePosition;
		}));
	};

	const handleDisplayModeChecked = (checked: boolean) => {
		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.ai.displayMode = checked;
		}));
	};

	const handleObjectColorSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.ai.objectColor = value as Colors;
		}));
	};

	const handleRuleColorSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.ai.ruleColor = value as Colors;
		}));
	};

	const handleEventColorSelect= (value: string | null) => {
		if (!value) {
			return;
		}

		setEmbeddedOsdSettingsDiff(produce(localEmbeddedOsdSettings, draft => {
			draft.ai.eventColor = value as Colors;
		}));
	};

	return (
		<SettingsLayout.Form className="embedded-osd-settings" onReset={ handleReset } onSubmit={ handleSubmit }>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Camera Information">
					<div className="form-row">
						<span>Resolution</span>
						<div className="form-switch-wrapper">
							<Switch
								checked={ resolution }
								onChange={ handleResolutionChecked }
							/>
						</div>
					</div>
					<div className="form-row">
						<span>Date / Time</span>
						<div className="form-switch-wrapper">
							<Switch
								checked={ dateTime }
								onChange={ handleDateTimeChecked }
							/>
						</div>
					</div>
					<div className="form-row">
						<div className="device-name-wrapper">
							<span>Device Name</span>
							<Popover content={ deviceNamePopover } wrapClassName="information-popover">
								<Icon iconWeight={ IconWeight.LIGHT } size={ IconSize.SMALLEST } name="circle-info" />
							</Popover>
						</div>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleDeviceNameSelect }
							options={ deviceNameOptions }
							value={ deviceName }
						/>
					</div>
				</Card>
				<Card size="small" title="AI OSD" popoverContent={ aiOsdPopover }>
					<div className="form-row">
						<span>Display Mode</span>
						<div className="form-switch-wrapper">
							<Switch
								checked={ displayMode }
								onChange={ handleDisplayModeChecked }
							/>
						</div>
					</div>
					<div className="form-row">
						<span>Object Color</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleObjectColorSelect }
							options={ colorOptions }
							value={ objectColor }
						/>
					</div>
					<div className="form-row">
						<span>Rule Color</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleRuleColorSelect }
							options={ colorOptions }
							value={ ruleColor }
						/>
					</div>
					<div className="form-row">
						<span>Event Color</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleEventColorSelect }
							options={ colorOptions }
							value={ eventColor }
						/>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(EmbeddedOsdSettings);
